import { ImagePlus, Loader2 } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  /** Called when the user picks/drops a file. The caller owns the upload mutation. */
  onSelect: (file: File) => void;
  /** Show a spinner + disable interaction while the caller's upload runs. */
  isUploading?: boolean;
  /** `accept` attribute for the file input. Defaults to all images. */
  accept?: string;
  /** Hard-disable the control. */
  disabled?: boolean;
  className?: string;
}

/**
 * Presentational dropzone for selecting a single image. It owns drag/highlight
 * state and a local preview of the chosen file, but does NOT fetch or upload —
 * mirror the `AsyncCombobox` posture and let the caller wire the mutation
 * (see `ProfileAvatarCard` for the reference usage).
 */
export function ImageUpload({
  onSelect,
  isUploading = false,
  accept = "image/png,image/jpeg,image/webp,image/gif",
  disabled = false,
  className,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);

  // Revoke the object URL whenever it changes or the component unmounts.
  useEffect(() => {
    if (!preview) return;
    return () => URL.revokeObjectURL(preview);
  }, [preview]);

  const locked = disabled || isUploading;

  function handleFile(file: File | undefined) {
    if (!file || locked) return;
    setPreview(URL.createObjectURL(file));
    onSelect(file);
  }

  return (
    <div className={className}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={locked}
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <button
        type="button"
        disabled={locked}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!locked) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        className={cn(
          "flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center transition-colors",
          dragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50",
          locked && "pointer-events-none opacity-60",
        )}
      >
        {isUploading ? (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        ) : preview ? (
          <img src={preview} alt="Selected preview" className="h-16 w-16 rounded-md object-cover" />
        ) : (
          <ImagePlus className="h-6 w-6 text-muted-foreground" />
        )}
        <span className="text-sm font-medium">
          {isUploading ? "Uploading…" : "Click or drop an image"}
        </span>
        <span className="text-xs text-muted-foreground">PNG, JPEG, WebP or GIF</span>
      </button>
    </div>
  );
}
